import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiTrash2, FiShoppingCart, FiArrowLeft, FiCheck, FiAlertCircle,
  FiFileText, FiCreditCard, FiHome, FiPackage, FiLock
} from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { stripeConnectService } from '../services/stripeConnectService';
import { supabase } from '../lib/supabase';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart, getCartTotal, getDepositTotal } = useCart();
  const [step, setStep] = useState('cart');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TX',
    zip: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleProceedToCheckout = () => {
    if (!agreedToTerms) {
      alert('Please read and agree to the Conditions of Sale before proceeding.');
      return;
    }
    setStep('customer-info');
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all required fields.');
      return;
    }
    setStep('payment');
  };

  const [paymentError, setPaymentError] = useState('');

  const handlePayment = async () => {
    setIsProcessing(true);
    setPaymentError('');
    
    try {
      // Get the dealer for the first cart item (most carts will be single-dealer)
      const firstItem = cartItems[0];
      const shedId = firstItem?.id;
      
      if (!shedId) {
        throw new Error('No items in cart');
      }

      // Look up the dealer for this shed
      const { data: dealer, error: dealerErr } = await stripeConnectService.getDealerForShed(shedId);
      
      if (dealerErr || !dealer) {
        throw new Error('Could not find dealer for this shed. Please contact us directly.');
      }

      if (!dealer.stripe_connected) {
        throw new Error('This dealer has not set up payment processing yet. Please contact us at (682) 204-0561 to complete your purchase.');
      }

      // Build a combined line item name for multi-shed carts
      const shedName = cartItems.length === 1 
        ? (firstItem.name || `${firstItem.size} ${firstItem.shedType}`)
        : `${cartItems.length} Sheds — Deposit`;

      // Create the checkout session through the edge function
      const { data, error } = await stripeConnectService.createCheckoutSession({
        shed: {
          ...firstItem,
          name: shedName,
          depositAmount: getDepositTotal(),
          salePrice: getCartTotal(),
          price: getCartTotal()
        },
        dealer,
        customerEmail: customerInfo.email,
        customerName: customerInfo.name || '',
        customerPhone: customerInfo.phone || '',
        paymentType: 'deposit'
      });

      if (error) throw error;

      if (data?.url) {
        // Direct redirect to Stripe's hosted checkout
        window.location.href = data.url;
        return;
      }
      
      if (data?.session_id) {
        const result = await stripeConnectService.redirectToCheckout(data.session_id, data.url);
        if (result.error) throw result.error;
        return;
      }

      throw new Error('No checkout session created. Please try again.');
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const conditionsOfSale = `
CONDITIONS OF SALE - SHEDX

Please read these terms carefully before completing your purchase.

1. DEPOSIT REQUIREMENT
A 25% non-refundable deposit is required to reserve your shed and begin the ordering/delivery process. The remaining 75% balance is due upon delivery and setup completion.

2. DELIVERY & SETUP
- Free delivery is included within 50 miles of our location.
- Additional mileage fees apply beyond 50 miles ($3/mile).
- Customer is responsible for ensuring the delivery path is clear and accessible.
- A level site pad (gravel, concrete, or compacted base) is required.
- Sheds cannot be delivered in wet conditions that prevent safe access.

3. SITE PREPARATION
Customer is responsible for site preparation including:
- Level ground (within 3 inches across the entire area)
- Clear path at least 2 feet wider than the shed
- No overhead obstructions (power lines, tree branches)
- Ground must support the weight of the delivery truck

4. PERMITS & HOA APPROVAL
Customer is responsible for obtaining any required building permits and HOA approvals before delivery. ShedX is not liable for permit violations.

5. CUSTOMIZATION & CHANGES
- Custom orders cannot be canceled once production begins.
- Changes to orders must be made in writing within 24 hours of deposit.
- Additional charges may apply for modifications.

6. WARRANTY
- All new sheds include manufacturer warranty (varies by model).
- Warranty covers defects in materials and workmanship.
- Warranty does not cover normal wear, weather damage, or improper maintenance.
- Customer must register warranty within 30 days of delivery.

7. CANCELLATION POLICY
- Deposits are non-refundable once the order is placed.
- In-stock sheds: Cancellation within 24 hours may be eligible for deposit credit.
- Custom orders: No cancellations once production begins.

8. LIMITATION OF LIABILITY
ShedX's liability is limited to the purchase price of the shed. We are not liable for consequential damages, loss of use, or other indirect damages.

9. DISPUTE RESOLUTION
Any disputes shall be resolved through binding arbitration in the State of Texas.

By signing below (or checking the agreement box), you acknowledge that you have read, understand, and agree to these Conditions of Sale.
`;

  if (cartItems.length === 0 && step === 'cart') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <Helmet>
          <title>Shopping Cart - ShedX</title>
        </Helmet>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiShoppingCart className="w-10 h-10 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
            <p className="text-gray-600 mb-8">
              Browse our selection of quality storage sheds and add them to your cart.
            </p>
            <Link
              to="/sheds"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              <FiPackage className="w-5 h-5" />
              Browse Sheds
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <Helmet>
        <title>Checkout - ShedX</title>
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Progress Steps */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-center gap-4">
            {['cart', 'customer-info', 'payment'].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${
                  step === s ? 'text-emerald-600' : 
                  ['cart', 'customer-info', 'payment'].indexOf(step) > i ? 'text-emerald-600' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s ? 'bg-emerald-600 text-white' :
                    ['cart', 'customer-info', 'payment'].indexOf(step) > i ? 'bg-emerald-600 text-white' : 'bg-gray-200'
                  }`}>
                    {['cart', 'customer-info', 'payment'].indexOf(step) > i ? <FiCheck className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="hidden sm:inline font-medium">
                    {s === 'cart' ? 'Review' : s === 'customer-info' ? 'Info' : 'Payment'}
                  </span>
                </div>
                {i < 2 && <div className={`w-12 h-0.5 ${
                  ['cart', 'customer-info', 'payment'].indexOf(step) > i ? 'bg-emerald-600' : 'bg-gray-200'
                }`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Cart Review & Invoice */}
            {step === 'cart' && (
              <motion.div
                key="cart"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Cart Items */}
                  <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Your Cart</h2>
                      <div className="divide-y">
                        {cartItems.map((item) => (
                          <div key={item.id} className="py-4 flex gap-4">
                            <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {item.images?.[0] ? (
                                <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FiHome className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{item.name}</h3>
                              <p className="text-sm text-gray-500">{item.size} • {item.shedType}</p>
                              <div className="mt-2">
                                {item.salePrice && item.salePrice < item.price ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-400 line-through text-sm">{formatPrice(item.price)}</span>
                                    <span className="text-emerald-600 font-bold">{formatPrice(item.salePrice)}</span>
                                  </div>
                                ) : (
                                  <span className="text-emerald-600 font-bold">{formatPrice(item.price)}</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <FiTrash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Invoice Preview */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <FiFileText className="w-5 h-5 text-emerald-600" />
                        <h2 className="text-xl font-bold text-gray-900">Invoice Preview</h2>
                      </div>
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg text-emerald-700">SHEDX</h3>
                            <p className="text-sm text-gray-600"></p>
                            <p className="text-sm text-gray-600"> </p>
                            <p className="text-sm text-gray-600"></p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Invoice Date</p>
                            <p className="font-medium">{new Date().toLocaleDateString()}</p>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-300">
                              <th className="text-left py-2">Item</th>
                              <th className="text-right py-2">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cartItems.map((item) => (
                              <tr key={item.id} className="border-b border-gray-200">
                                <td className="py-2">
                                  <div>{item.name}</div>
                                  <div className="text-gray-500 text-xs">{item.size} {item.shedType}</div>
                                </td>
                                <td className="text-right py-2">{formatPrice(item.salePrice || item.price)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-b border-gray-300">
                              <td className="py-2 font-medium">Total Price</td>
                              <td className="text-right py-2 font-medium">{formatPrice(getCartTotal())}</td>
                            </tr>
                            <tr className="bg-emerald-50">
                              <td className="py-3 font-bold text-emerald-700">Deposit Due Today (25%)</td>
                              <td className="text-right py-3 font-bold text-emerald-700 text-lg">{formatPrice(getDepositTotal())}</td>
                            </tr>
                            <tr>
                              <td className="py-2 text-gray-600">Balance Due at Delivery</td>
                              <td className="text-right py-2 text-gray-600">{formatPrice(getCartTotal() - getDepositTotal())}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    {/* Conditions of Sale */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Conditions of Sale</h2>
                      <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto text-sm text-gray-700 whitespace-pre-line border mb-4">
                        {conditionsOfSale}
                      </div>
                      
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-gray-700">
                          <strong>I have read, understand, and agree to the Conditions of Sale.</strong>
                          <br />
                          <span className="text-sm text-gray-500">
                            I understand that the 25% deposit is non-refundable and the remaining balance is due at delivery.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Order Summary Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                      <h3 className="font-bold text-lg text-gray-900 mb-4">Order Summary</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Subtotal ({cartItems.length} items)</span>
                          <span className="font-medium">{formatPrice(getCartTotal())}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Delivery & Setup</span>
                          <span className="text-emerald-600 font-medium">FREE</span>
                        </div>
                        <div className="border-t pt-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total</span>
                            <span className="font-bold">{formatPrice(getCartTotal())}</span>
                          </div>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-4 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-emerald-800">Due Today (25%)</span>
                            <span className="text-xl font-bold text-emerald-700">{formatPrice(getDepositTotal())}</span>
                          </div>
                          <p className="text-xs text-emerald-600 mt-1">
                            Balance of {formatPrice(getCartTotal() - getDepositTotal())} due at delivery
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleProceedToCheckout}
                        disabled={!agreedToTerms}
                        className={`w-full mt-6 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-colors ${
                          agreedToTerms 
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <FiCreditCard className="w-5 h-5" />
                        Proceed to Checkout
                      </button>
                      
                      {!agreedToTerms && (
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <FiAlertCircle className="w-3 h-3" />
                          Please agree to the Conditions of Sale
                        </p>
                      )}

                      <Link
                        to="/sheds"
                        className="flex items-center justify-center gap-2 text-gray-600 hover:text-emerald-600 mt-4 text-sm"
                      >
                        <FiArrowLeft className="w-4 h-4" />
                        Continue Shopping
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Customer Information */}
            {step === 'customer-info' && (
              <motion.div
                key="customer-info"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Customer Information</h2>
                      <form onSubmit={handleProceedToPayment} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                              type="text"
                              name="firstName"
                              required
                              value={customerInfo.firstName}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                              type="text"
                              name="lastName"
                              required
                              value={customerInfo.lastName}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                              type="email"
                              name="email"
                              required
                              value={customerInfo.email}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                            <input
                              type="tel"
                              name="phone"
                              required
                              value={customerInfo.phone}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                          <input
                            type="text"
                            name="address"
                            value={customerInfo.address}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            placeholder="Street address for shed delivery"
                          />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                            <input
                              type="text"
                              name="city"
                              value={customerInfo.city}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              name="state"
                              value={customerInfo.state}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                            <input
                              type="text"
                              name="zip"
                              value={customerInfo.zip}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                          <button
                            type="button"
                            onClick={() => setStep('cart')}
                            className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors"
                          >
                            Continue to Payment
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                      <h3 className="font-bold text-lg text-gray-900 mb-4">Order Summary</h3>
                      <div className="space-y-2 text-sm mb-4">
                        {cartItems.map(item => (
                          <div key={item.id} className="flex justify-between">
                            <span className="text-gray-600 truncate pr-2">{item.name}</span>
                            <span className="font-medium">{formatPrice(item.salePrice || item.price)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t pt-3">
                        <div className="bg-emerald-50 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-emerald-800">Due Today</span>
                            <span className="text-xl font-bold text-emerald-700">{formatPrice(getDepositTotal())}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Payment */}
            {step === 'payment' && (
              <motion.div
                key="payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-emerald-800">Deposit Amount</span>
                          <span className="text-2xl font-bold text-emerald-700">{formatPrice(getDepositTotal())}</span>
                        </div>
                        <p className="text-sm text-emerald-600 mt-1">
                          25% deposit to reserve your shed. Balance due at delivery.
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-5 mb-6 text-center">
                        <FiLock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-700 font-medium mb-1">Secure Stripe Checkout</p>
                        <p className="text-xs text-gray-500">
                          You'll be redirected to Stripe's secure payment page to enter your card details. Your payment info never touches our servers.
                        </p>
                      </div>

                      {paymentError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2 mb-4">
                          <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <span>{paymentError}</span>
                        </div>
                      )}

                      <div className="flex gap-4 pt-2">
                        <button
                          type="button"
                          onClick={() => setStep('customer-info')}
                          className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          onClick={handlePayment}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white py-3 rounded-lg font-semibold transition-colors"
                        >
                          {isProcessing ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Redirecting to Stripe...
                            </>
                          ) : (
                            <>
                              <FiLock className="w-5 h-5" />
                              Pay {formatPrice(getDepositTotal())} Deposit
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                      <h3 className="font-bold text-lg text-gray-900 mb-4">Order Summary</h3>
                      <div className="space-y-2 text-sm mb-4">
                        {cartItems.map(item => (
                          <div key={item.id} className="flex justify-between">
                            <span className="text-gray-600 truncate pr-2">{item.name}</span>
                            <span className="font-medium">{formatPrice(item.salePrice || item.price)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t pt-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total</span>
                          <span>{formatPrice(getCartTotal())}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Balance Due at Delivery</span>
                          <span>{formatPrice(getCartTotal() - getDepositTotal())}</span>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-4 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-emerald-800">Paying Now</span>
                            <span className="text-xl font-bold text-emerald-700">{formatPrice(getDepositTotal())}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
